/**
 * @fileoverview Company card item component for the companies list view
 * @active Used in current Next.js App Router routes
 * 
 * This component renders an individual company card in the table view's card mode.
 * It displays the company logo, name, accelerator information, and other company details.
 * 
 * COMPONENT HIERARCHY:
 * - newCompaniesList/index.tsx: Main table view component
 *   └─ companiesCards/index.tsx: Card view container
 *      └─ companyCardItem/index.tsx (this file): Individual company card
 *         ├─ AcceleratorCardView.tsx: Displays accelerator information
 *         └─ CompanyCardInfo.tsx: Displays company details
 * 
 * @see {@link src/components/companies/companiesList/newCompaniesList/index.tsx} - Parent table view
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesCards/index.tsx} - Card container
 * @see {@link src/components/companies/companiesList/newCompaniesList/types.ts} - Type definitions
 */

import type { ReactElement } from "react";

import { Flex, Text } from "@chakra-ui/react";
import Link from "next/link";

import DefaultImage from "@/components/ui/DefaultImage";

import type { CompanyItem } from "../../types";

import { AcceleratorCardView } from "./AcceleratorCardView";
import CompanyCardInfo from "./CompanyCardInfo";

/**
 * Props interface for the CompanyCardItem component
 */
interface CompanyCardItemProps {
  /** Company data to display in the card */
  item: CompanyItem;
}

/**
 * Renders an individual company card with logo, name, and details
 * @active Used in current Next.js App Router routes
 * 
 * This component displays a company card with the following elements:
 * - Company logo and name with a link to the company details page
 * - Accelerator information if available
 * - Company details such as description, stage, location, etc.
 * 
 * @param {CompanyCardItemProps} props - Component props
 * @returns {ReactElement} Rendered company card
 * 
 * @see {@link ./AcceleratorCardView.tsx} - Displays accelerator information
 * @see {@link ./CompanyCardInfo.tsx} - Displays company details
 */
const CompanyCardItem = ({ item }: CompanyCardItemProps): ReactElement => {
  return (
    <Flex
      direction={"column"}
      borderWidth={"1px"}
      borderRadius={"4px"}
      borderColor={"lightGray"}
      bg={"dashboardBackground"}
      _hover={{ bg: "lightBlue2" }}
      p={"20px"}
      pt={"10px"}
      rowGap={"16px"}
    >
      <Flex direction={"column"} rowGap={"16px"} w={"100%"}>
        <Flex align={"center"} columnGap={"12px"} w={"fit-content"}>
          <Flex
            minW={"24px"}
            h={"24px"}
            borderRadius={"4px"}
            overflow={"hidden"}
          >
            <DefaultImage
              src={item?.company?.fileLogo || null}
              options={{
                objectFit: "contain",
                w: "24px",
                alt: item?.company?.companyName || "Company logo",
              }}
            />
          </Flex>
          <Link
            href={`/companies/${item?.company?.slug}`}
            aria-label={`View details for ${item?.company?.companyName}`}
          >
            <Text
              fontSize={"16px"}
              fontWeight={"700"}
              lineHeight={"1"}
              letterSpacing={"2%"}
            >
              {item?.company?.companyName}
            </Text>
          </Link>
        </Flex>
        <AcceleratorCardView item={item} />

        <CompanyCardInfo item={item} />
      </Flex>
    </Flex>
  );
};

export default CompanyCardItem;
